import type { Point } from '@ikegps/core-api';
import {
	CollectionModelDrawable,
	CollectionModelDrawableLine,
	CollectionModelDrawableLinePoint,
	CollectionModelDrawableMarker,
	CollectionModelDrawableMarkerOffset,
	CollectionModelDrawableVerticalSeparation,
	type DrawableWithLabel,
	MoveableDrawable,
} from '../../model';
import { IkeDraw } from '../draw';
import { IkeDrawTool } from '../draw.state';
import { LabelSVGDraw } from '../drawable/label.draw.svg';
import { IkeDrawLabel } from '../drawable/util.draw.svg';
import { movePoint } from '../util';

export interface SelectionMoveEvent {
	label?: string;
	delta: {
		x: number;
		y: number;
	};
	$event: Event;
}

export function selectionMoveBinding(dc: IkeDraw, evt: SelectionMoveEvent) {
	const delta = evt.delta;
	if (evt.label === 'image') {
		const centerX = dc.state.centerX;
		const centerY = dc.state.centerY;
		const newPoint = movePoint(
			{ x: centerX, y: centerY },
			// Moving images is in the opposite direction of the mouse movements
			{ x: -delta.x, y: -delta.y },
			dc.capture.resolution
		);

		dc.state.setCenter(newPoint.x, newPoint.y);
		return;
	}

	const currentId = dc.state.selectedId;
	if (currentId == null) {
		return;
	}

	if (evt.$event) {
		evt.$event.preventDefault();
	}

	// If we have the base tool selected, we cannot move any drawables
	if (dc.state.tool === IkeDrawTool.BASE || dc.state.tool === IkeDrawTool.LINE) {
		return;
	}

	let drawableToMove = dc.selected;

	if (evt.label === IkeDrawLabel.LABEL) {
		if (
			CollectionModelDrawableMarker.isMarker(drawableToMove) ||
			CollectionModelDrawableLine.isLine(drawableToMove) ||
			drawableToMove instanceof CollectionModelDrawableMarkerOffset ||
			drawableToMove instanceof CollectionModelDrawableVerticalSeparation
		) {
			return moveLabel(dc, drawableToMove, delta);
		}
	}

	// Dragging a point in the line references the point rather than the line element its self. so we need to target the parent instead
	if (CollectionModelDrawableLinePoint.isLinePoint(drawableToMove)) {
		drawableToMove = drawableToMove.parent;
	}

	if (drawableToMove.isApprovableInsightDrawable()) {
		drawableToMove.approveInsightFlag();
	}

	if (CollectionModelDrawableLine.isLine(drawableToMove)) {
		if (evt.label === IkeDrawLabel.START || evt.label === null) {
			drawableToMove = drawableToMove.start;
		} else if (evt.label === IkeDrawLabel.END) {
			drawableToMove = drawableToMove.end;
		}
	}

	const moveTo = movePoint(drawableToMove, delta, dc.capture.resolution);

	if (drawableToMove instanceof MoveableDrawable) {
		return drawableToMove.move(moveTo.x, moveTo.y);
	}
}

function moveLabel(dc: IkeDraw, drawable: CollectionModelDrawable & DrawableWithLabel, delta: Point) {
	const label = drawable.label;

	const uiScale = dc.state.scale;
	const moveTo = movePoint(label, delta, dc.capture.resolution, {
		width: LabelSVGDraw.Size.width * uiScale,
		height: LabelSVGDraw.height(1) * uiScale,
	});

	label.move(moveTo.x, moveTo.y);
}
